/**
 * useBodyScrollLock — the ONE way to freeze page scroll behind an overlay
 * (fullscreen chat, file/3D preview modals, confirmation popups).
 *
 * Why shared + reference-counted (2026-08-12, thread-scrolling UX round):
 * several surfaces used to save/restore `document.body.style.overflow`
 * independently. That naive pattern breaks the moment two of them overlap
 * with a non-LIFO open/close order — whichever closes last restores the
 * snapshot it took while the OTHER lock was already active, permanently
 * locking (or unlocking) the page. Here the first acquirer snapshots the
 * real pre-lock styles, every later acquirer only bumps a counter, and the
 * snapshot is restored exactly once when the LAST lock releases.
 *
 * The lock also reserves the page scrollbar's width as body padding so the
 * content behind a translucent overlay doesn't shift sideways when the
 * scrollbar disappears, and stamps `overscroll-behavior: none` on <html> so
 * wheel/touch chaining can't rubber-band the page while locked.
 */
import { useEffect } from 'react';

let lockCount = 0;
let savedBodyOverflow = '';
let savedBodyPaddingRight = '';
let savedHtmlOverflow = '';
let savedHtmlOverflowPriority = '';
let savedHtmlOverscrollBehavior = '';

/**
 * Imperative variant for non-React callers. Returns a release function;
 * calling it more than once is safe (only the first call counts).
 */
export function acquireBodyScrollLock(): () => void {
  if (typeof document === 'undefined') return () => {};

  if (lockCount === 0) {
    const body = document.body;
    const html = document.documentElement;
    savedBodyOverflow = body.style.overflow;
    savedBodyPaddingRight = body.style.paddingRight;
    savedHtmlOverflow = html.style.getPropertyValue('overflow');
    savedHtmlOverflowPriority = html.style.getPropertyPriority('overflow');
    savedHtmlOverscrollBehavior = html.style.overscrollBehavior;

    // Reserve the vanishing scrollbar's width so the page doesn't shift.
    // Clamped to a sane rail width — degenerate layout readings (jsdom,
    // mid-zoom reflow) must never inject page-wide padding.
    const scrollbarWidth = Math.max(0, Math.min(40, window.innerWidth - html.clientWidth));
    if (scrollbarWidth > 0) {
      const currentPadding = parseFloat(window.getComputedStyle(body).paddingRight) || 0;
      body.style.paddingRight = `${currentPadding + scrollbarWidth}px`;
    }
    body.style.overflow = 'hidden';
    // The DOCUMENT ELEMENT must be locked too — this is the load-bearing
    // half (2026-08-12 production report). app.css sets
    // `html { overflow-x: hidden }`, and once <html>'s overflow is not
    // fully `visible` the viewport stops honoring <body>'s overflow
    // entirely (CSS overflow propagation rules), so a body-only lock
    // leaves the native window scrollbar alive and scrolling behind the
    // fullscreen chat. `!important` so no stylesheet can out-rank the
    // lock while it's held.
    html.style.setProperty('overflow', 'hidden', 'important');
    html.style.overscrollBehavior = 'none';
  }
  lockCount += 1;

  let released = false;
  return () => {
    if (released) return;
    released = true;
    lockCount = Math.max(0, lockCount - 1);
    if (lockCount === 0 && typeof document !== 'undefined') {
      const html = document.documentElement;
      document.body.style.overflow = savedBodyOverflow;
      document.body.style.paddingRight = savedBodyPaddingRight;
      if (savedHtmlOverflow) {
        html.style.setProperty('overflow', savedHtmlOverflow, savedHtmlOverflowPriority);
      } else {
        html.style.removeProperty('overflow');
      }
      html.style.overscrollBehavior = savedHtmlOverscrollBehavior;
    }
  };
}

/** Lock page scroll while `active` is true; releases on unmount. */
export function useBodyScrollLock(active: boolean): void {
  useEffect(() => {
    if (!active) return undefined;
    return acquireBodyScrollLock();
  }, [active]);
}

/** How many locks are currently held — exposed for tests/diagnostics. */
export function getBodyScrollLockCount(): number {
  return lockCount;
}

/** Test-only: drop all locks and forget snapshots (jsdom isolation). */
export function __resetBodyScrollLockForTests(): void {
  lockCount = 0;
  savedBodyOverflow = '';
  savedBodyPaddingRight = '';
  savedHtmlOverflow = '';
  savedHtmlOverflowPriority = '';
  savedHtmlOverscrollBehavior = '';
}
